import qs from 'qs';
import axios from 'axios';
import { getTokenInterceptor, refreshTokenInterceptor, debugRequests, debugResponses, getEconnresetInterceptor } from './interceptors/index.js';
import agents from './agents/index.js';
import { getCorrelationId } from './correlation-id.js';

/**
 * Base class for all Fabric API clients.
 */
export default class BaseApiClient {
  /**
   * Initialise.
   */
  constructor({
    baseURL,
    mimeType,
    getToken,
    refreshToken,
    debug = true,
    getLogLevel,
    getExtraLogAttributes,
    logger,
    apiName,
    defaultHeaders,
    correlationId,
    qsOptions = {
      arrayFormat: 'brackets'
    }
  } = {}) {
    if (!baseURL) {
      throw new TypeError('A baseURL must be provided.');
    }
    const headers = defaultHeaders || {
      'Content-Type': 'application/json',
      accept: mimeType || 'application/json',
      'X-Correlation-ID': getCorrelationId(correlationId)
    };
    const axiosOpts = {
      baseURL,
      headers,
      // Needed to serialize params such as unixUpdated[gt]=123
      paramsSerializer: params => qs.stringify(params, qsOptions)
    };

    // Keep sockets around even when there are no outstanding requests, so they
    // can be used for future requests without having to re-establish a TCP
    // connection.
    if (agents) {
      axiosOpts.httpAgent = agents.httpAgent;
      axiosOpts.httpsAgent = agents.httpsAgent;
    }
    this.axios = axios.create(axiosOpts);
    this.setupInterceptors({
      getToken,
      refreshToken,
      debug,
      getLogLevel,
      getExtraLogAttributes,
      logger,
      apiName
    });
  }

  /**
   * Setup any request and response interceptors.
   */
  setupInterceptors({
    getToken,
    refreshToken,
    debug,
    getLogLevel,
    getExtraLogAttributes,
    logger,
    apiName
  }) {
    if (getToken) {
      this.setupGetTokenInterceptor({
        getToken
      });
    }
    if (refreshToken) {
      this.setupRefreshTokenInterceptor({
        refreshToken
      });
    }
    if (debug) {
      this.setupDebugInterceptors(logger, {
        apiName,
        getLogLevel,
        getExtraLogAttributes
      });
    }
    if (agents) {
      this.setupEconnresetInterceptor();
    }
  }

  /**
   * Setup the refresh token interceptor.
   */
  setupRefreshTokenInterceptor({
    refreshToken
  }) {
    const interceptor = refreshTokenInterceptor({
      refreshToken
    });
    this.axios.interceptors.response.use(interceptor.success, interceptor.error);
  }

  /**
   * Setup the get token interceptor.
   */
  setupGetTokenInterceptor({
    getToken
  }) {
    const interceptor = getTokenInterceptor({
      getToken
    });
    this.axios.interceptors.request.use(interceptor.success, interceptor.error);
  }

  /**
   * Setup the debug interceptor.
   */
  setupDebugInterceptors(logger, {
    apiName,
    getLogLevel,
    getExtraLogAttributes
  }) {
    const requestInterceptor = debugRequests();
    const responseInterceptor = debugResponses(logger, {
      apiName,
      getLogLevel,
      getExtraLogAttributes
    });
    this.axios.interceptors.request.use(requestInterceptor.success, requestInterceptor.error);
    this.axios.interceptors.response.use(responseInterceptor.success, responseInterceptor.error);
  }

  /**
   * Setup the ECONNRESET interceptor.
   */
  setupEconnresetInterceptor() {
    const interceptor = getEconnresetInterceptor();
    this.axios.interceptors.response.use(interceptor.success, interceptor.error);
  }
  async request(endpoint, options) {
    const res = await this.axios.request(endpoint, options);
    const {
      data
    } = res;
    return data;
  }
  async get(endpoint, options) {
    return this.request(endpoint, {
      ...options,
      method: 'GET'
    });
  }
  async post(endpoint, options) {
    return this.request(endpoint, {
      ...options,
      method: 'POST'
    });
  }
  async put(endpoint, options) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT'
    });
  }
  async delete(endpoint, options) {
    return this.request(endpoint, {
      ...options,
      method: 'DELETE'
    });
  }
}